/** A helper class to build and interact with the massive JS strings for our Chat Providers
 * @constructor providerId - The provider ID for the chat provider
 */
export class ChatProviderHelper {
  /**
   * Fetches the fully formed Eltropy snippet
   * @param providerId - The provider ID for the chat provider
   * @param source - "Source" of js - this allows flexibility for pointing at different snippets in different envirs, changing quickly, etc.
   * @returns the fully formed Eltropy snippet
   */
  public static getEltropySnippet(providerId: string, source: string): string {
    /** NOTE: THIS IS GLOBAL, NOT LENDER SPECIFIC. ELTROPY SAID THIS WILL BE MIGRATED IN THE COMING ~12 MONTHS.*/
    const ELTROPY_SRC = source;
    /** The snippet itself, for interpolation */
    const snippet = `(function(e,l,t,r,o,p){e[r]=e[r]||{init:function(){e[r].q=arguments}};
    o=l.createElement(t);p=l.getElementsByTagName(t)[0];o.async=1;
    o.src='${ELTROPY_SRC}';p.parentNode.insertBefore(o,p);})
    (window,document,'script','Eltropy');
    Eltropy.init({domain:'${providerId}'})`;
    return snippet;
  }

  /**
   * Fetches the fully formed LivePerson snippet - NOTE: This snippet is a bit old, may need to upgrade if we onboard
   * @returns fully formed LivePerson Snipet
   */
  public static getLivePersonSnippet(providerId: string): string {
    /** The snippet itself, for interpolation */
    const snippet = `window.lpTag=window.lpTag||{},
  'undefined'==typeof window.lpTag._tagCount?(window.lpTag={wl:lpTag.wl||null,
    scp:lpTag.scp||null,
    site:${providerId}||'',
    section:lpTag.section||'',
    tagletSection:lpTag.tagletSection||null,
    autoStart:lpTag.autoStart!==!1,
    ovr:lpTag.ovr||{},
    _v:'1.10.0',
    _tagCount:1,
    protocol:'https:',
    events:{bind:function(t,e,i){lpTag.defer(function(){lpTag.events.bind(t,e,i)},0)},
    trigger:function(t,e,i){lpTag.defer(function(){lpTag.events.trigger(t,e,i)},1)}},
    defer:function(t,e){0===e?(this._defB=this._defB||[],
      this._defB.push(t)):1===e?(this._defT=this._defT||[],
        this._defT.push(t)):(this._defL=this._defL||[],this._defL.push(t))},
        load:function(t,e,i){var n=this;setTimeout(function(){n._load(t,e,i)},0)},
        _load:function(t,e,i){var n=t;t||(n=this.protocol+'//'
        +(this.ovr&&this.ovr.domain?this.ovr.domain:'lptag.liveperson.net')+'/tag/tag.js?site='+this.site);
        var o=document.createElement('script');
        o.setAttribute('charset',e?e:'UTF-8'),
        i&&o.setAttribute('id',i),o.setAttribute('src',n),
        document.getElementsByTagName('head').item(0).appendChild(o)},init:function(){this._timing=this._timing||{},
        this._timing.start=(new Date).getTime();
        var t=this;
        window.attachEvent?window.attachEvent('onload',function(){t._domReady('domReady')}):(window.addEventListener('DOMContentLoaded',
        function(){t._domReady('contReady')},!1),
        window.addEventListener('load',function(){t._domReady('domReady')},!1)),
        'undefined'===typeof window._lptStop&&this.load()},
        start:function(){this.autoStart=!0},
        _domReady:function(t){this.isDom||(this.isDom=!0,
          this.events.trigger('LPT','DOM_READY',
          {t:t})),this._timing[t]=(new Date).getTime()},
          vars:lpTag.vars||[],dbs:lpTag.dbs||[],ctn:lpTag.ctn||[],
          sdes:lpTag.sdes||[],hooks:lpTag.hooks||[],
          identities:lpTag.identities||[],ev:lpTag.ev||[]},lpTag.init()):window.lpTag._tagCount+=1;`;
    return snippet;
  }

  /**
   * Fetches the Glia cdn (they need flexibility to change the underlying snippet for envir purposes)
   * @returns the fully formed Glia cdn src
   * @remarks Glia handles Origin URL mapping on their side, so much not is needed here
   */
  public static getGliaSrc(): string {
    const src = 'https://api.glia.com/salemove_integration.js';
    return src;
  }

  /**
   * Fetches the fully formed Boost.ai Chat Panel snippet.
   *
   * This helper injects the vendor script from the provided Boost.ai tenant URL,
   * initializes the Chat Panel, and programmatically opens it.
   *
   * @param clientUrl The full Boost.ai tenant URL (for example,
   *   "https://adminpanel-msufcu-fran.boost.ai").
   * @returns A self-invoking snippet that injects and initializes the Chat Panel.
   *
   * @remarks
   * Supported interactions (from Boost.ai Chat Panel docs):
   * - Panel control: `chatPanel.show()`, `hide()`, `toggle()`, `minimize()`, `maximize()`, `close()`; can open on load.
   * - Configuration via `options.chatPanel`: header (title/branding), styling (theme/pace/colors/placement/size), locale,
   *   `requestFeedback`, visibility of minimize/close buttons.
   * - Context/routing: `filterValues` and custom payload/context for skill/intent routing.
   * - Events & lifecycle: `onReady`, `onOpen`, `onClose`, and unread/visibility handlers where supported.
   * - Conversation utilities: start/end/reset conversation and programmatic events when exposed by your panel version.
   *
   * Security and environment (see instructions.md):
   * - Boost.ai must allowlist your origins (e.g., `localhost:4200`, dev/QA/UAT/prod domains) or the script request can return 403.
   * - Update CSP to allow Boost.ai: add to `script-src`, `connect-src`, `frame-src` (if iframed), and `style-src` as needed.
   *
   * Admin configuration in arcDX:
   * - Provider: `boostai`. Store the full tenant URL in `providerId`.
   * - Script is injected dynamically at runtime; do not place tags in `index.html`.
   */
  public static getBoostAiSnippet(clientUrl: string): string {
    const snippet = `(function() {
      var s = document.createElement('script');
      s.src = '${clientUrl}/chatPanel/chatPanel.js';
      s.async = true;
      s.onload = function() {
        try {
          if (typeof window.boostInit !== 'function') {
            console.error('Boost.ai: boostInit is not defined after script load');
            return;
          }
          var options = { chatPanel: {} };
          var boost = window.boostInit('${clientUrl}', options);
          if (boost && boost.chatPanel && typeof boost.chatPanel.show === 'function') {
            boost.chatPanel.show();
          } else {
            console.error('Boost.ai: chatPanel.show is not available', boost);
          }
        } catch (e) {
          console.error('Boost.ai: error initializing chat panel', e);
        }
      };
      s.onerror = function(e) {
        console.error('Boost.ai: failed to load chatPanel.js from ${clientUrl}', e);
      };
      document.body.appendChild(s);
    })();`;
    return snippet;
  }
}
